import { useContext, useEffect, useRef, useState } from "react"
import { Toast } from "primereact/toast"
import { Button } from "primereact/button"
import { Dialog } from "primereact/dialog"
import axios from "axios"
import styles from "../../../styles/Participant-Employment.module.css"
import { engagementChannels } from "../../../components/Appointments/utils.js"
import { employmentApiUrl } from "../../../config/microservicesUrls"
import { LoaderMedium } from "../../../components/Loaders"
import EmploymentReferral from "./ReferralForm"
import TransitionPlan from "../TransitionPlan"
import {
  showError,
  DateFormat,
  formatToLocalDateTime,
  getDuration,
  renderTableDataCell,
  getStartAndEndTime,
  Actions,
  deleteActiveReferral,
  groupBy,
} from "../Utils/Utils"
import { YesNoUnknown, YesNo } from "../Types/Dropdowns"
import { UserContext } from "../../../context/userContext"
import ViewHeader from "../Utils/ViewHeader.js"

const api_url = employmentApiUrl()

export default function EmploymentReferralIndex({
  referrals,
  setReferrals,
  loading,
  updateIsActiveReferral,
  updateHasCareerProfile,
  inactive,
  setViewMode,
  viewMode,
  showReferralForm,
  setShowReferralForm,
  activeReferral,
  referralFormAction,
  setReferralFormAction,
  selectedReferral,
  setSelectedReferral,
  showTransitionPlan,
  setShowTransitionPlan,
  updateSelectedReferral,
  closeViewMode,
  setTransitionPlan,
  transitionPlan,
  updateData,
}) {
  const toast = useRef(null)
  const [
    deleteReferralConfirmationVisible,
    setDeleteReferralConfirmationVisible,
  ] = useState(false)
  const [reopenConfirmationVisible, setReopenConfirmationVisible] =
    useState(false)
  const [completedReferrals, setCompletedReferrals] = useState([])

  const userCtx = useContext(UserContext)

  useEffect(() => {
    if (activeReferral) {
      updateIsActiveReferral(true)
      updateHasCareerProfile(activeReferral.hasCareerProfile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReferral])

  useEffect(() => {
    setCompletedReferrals(
      referrals
        .filter((r) => r.completedAt != null)
        .sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt)),
    )
  }, [referrals])

  const renderReferalActions = (referral, lastItem) => {
    return (
      <div className={styles.table_actions}>
        <Button
          icon="pi pi-eye"
          className={styles.rounded_btn}
          onClick={async () => {
            setReferralFormAction(Actions.View)
            updateSelectedReferral(referral)
            setShowReferralForm(true)
            setViewMode({ view: true, referral: referral })
          }}
        />
        {activeReferral == null && lastItem && (
          <Button
            className={styles.reopen_btn}
            onClick={() => {
              setReferralFormAction(Actions.Edit)
              setReopenConfirmationVisible(true)
            }}
            disabled={inactive}
          />
        )}
      </div>
    )
  }

  const deleteReferral = () => {
    axios
      .delete(
        `${api_url}/referral/?referralId=${activeReferral.id}
      &organizationId=${activeReferral.organizationId}
      &participantId=${activeReferral.participantId}`,
        {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        },
      )
      .then(function (response) {
        const updatedReferrals = deleteActiveReferral(referrals)
        setReferrals(updatedReferrals)
        updateIsActiveReferral(false)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        showError(toast, "Not able to delete active referral", errors)
      })
  }

  const renderDeleteReferralConfirmation = () => {
    const footerContent = (
      <div className={styles.buttons}>
        <Button
          className={styles.cancel_button}
          label="No"
          onClick={() => setDeleteReferralConfirmationVisible(false)}
          autoFocus
        />
        <Button
          className={styles.button}
          label="Yes"
          onClick={() => {
            deleteReferral()
            setDeleteReferralConfirmationVisible(false)
          }}
        />
      </div>
    )
    return (
      <Dialog
        header="Delete confirmation"
        modal={false}
        visible={deleteReferralConfirmationVisible}
        className="custom_dialog"
        style={{ width: "50vw" }}
        onHide={() => setDeleteReferralConfirmationVisible(false)}
        footer={footerContent}
      >
        <p>{`Are you sure you want to delete the active referral?`}</p>
      </Dialog>
    )
  }

  const reopenReferral = () => {
    axios
      .put(
        `${api_url}/referral/reopen`,
        {
          updatedBy: userCtx.session.user.staff_id,
          organizationId: userCtx.session.user.organization_id,
          participantId: userCtx.current_participant_id,
        },
        {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        },
      )
      .then(function (response) {
        updateData(response.data)
        updateIsActiveReferral(true)
        setReopenConfirmationVisible(false)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        setReopenConfirmationVisible(false)
        showError(toast, "Not able to reopen referral", errors)
      })
  }

  const renderReopenConfirmation = () => {
    const footerContent = (
      <div className={styles.buttons}>
        <Button
          className={styles.cancel_button}
          label="No"
          onClick={() => setReopenConfirmationVisible(false)}
          autoFocus
        />
        <Button
          className={styles.button}
          label="Yes"
          onClick={() => {
            reopenReferral()
          }}
        />
      </div>
    )
    return (
      <Dialog
        header="Reopen confirmation"
        modal={false}
        visible={reopenConfirmationVisible}
        className="custom_dialog"
        style={{ width: "50vw" }}
        onHide={() => setReopenConfirmationVisible(false)}
        footer={footerContent}
      >
        <p>{`Are you sure you want to reopen the referral?`}</p>
      </Dialog>
    )
  }
  const renderActiveReferral = () => {
    const referral = activeReferral !== null ? activeReferral : selectedReferral
    const activeReferralDic = [
      {
        label: "Engagement date",
        value: referral?.engagementDate,
        type: "date",
        group: 1,
      },
      {
        label: "Engagement channel",
        value: engagementChannels[referral?.engagementChannel],
        type: "string",
        group: 1,
      },
      {
        label: "Engagement duration",
        value: getDuration(referral?.startTime, referral?.endTime),
        type: "string",
        group: 1,
      },
      {
        label: "Start/End time",
        value: getStartAndEndTime(referral?.startTime, referral?.endTime),
        type: "string",
        group: 1,
      },
      {
        label: "Date opened",
        value: referral?.createdAt,
        type: "datetime",
        group: 2,
      },
      {
        label: "Performed by",
        value: referral?.createdBy,

        type: "staff",
        group: 2,
      },
      {
        label: "Last updated at",
        value: referral?.updatedAt,
        type: "datetime",
        group: 2,
      },
      {
        label: "Last updated by",
        value: referral?.updatedBy,
        type: "staff",
        group: 2,
      },
      {
        label: "State vocational rehabilitation counselor",
        value: referral?.vocationalRehabCounselor,
        type: "string",
        group: 3,
      },
      {
        label: "Was referral sent to state vocational rehabilitation?",
        value: YesNoUnknown[referral?.refSentToVocRehab],
        type: "string",
        group: 3,
      },
      {
        label: "Other healthcare/social service providers",
        value: referral?.otherHealthServiceProviders,
        type: "html",
        group: 4,
      },
      {
        label: "What is the person saying about work?",
        value: referral?.workComments,
        type: "html",
        group: "additional_info",
      },
      {
        label:
          "Is the person interested in gaining more education now to advance their career goals?",
        value: referral?.interestInMoreEducation,
        type: "html",
        group: "additional_info",
      },
      {
        label:
          "Please include information about the person's illness (diagnosis if reported, symptoms, etc.)",
        value: referral?.infoAboutPersonsIllness,
        type: "html",
        group: "additional_info",
      },
      {
        label: "What are some of the person's strengths?",
        value: referral?.personsStrengths,
        type: "html",
        group: "additional_info",
      },
      {
        label:
          "What job do you and the individual think would be a good match?",
        value: referral?.jobMatchRecommendation,
        type: "html",
        group: "additional_info",
      },
    ]
    const basicInfo = groupBy(
      activeReferralDic.filter((r) => r.group !== "additional_info"),
      "group",
    )
    return (
      <div className={styles.rounded_wrapper}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginTop: "10px",
            marginBottom: "10px",
          }}
        >
          <ViewHeader
            viewMode={viewMode}
            title={"Employment referral"}
            closeViewMode={closeViewMode}
          />
          <div className={styles.referralButtonsContainer}>
            {checkTransitionPlanAvailability() && (
              <Button
                label="View transition plan"
                className={styles.transition_btn}
                onClick={() => {
                  const transitionPlan = viewMode
                    ? viewMode?.referral?.transitionPlan
                    : referral?.transitionPlan
                  setTransitionPlan(transitionPlan)
                  setShowTransitionPlan(true)
                }}
              />
            )}
          </div>
          {activeReferral != null && !viewMode && (
            <span>
              <Button
                className={styles.edit_btn}
                onClick={() => {
                  setReferralFormAction(Actions.Edit)
                  setSelectedReferral(activeReferral)
                  setShowReferralForm(true)
                }}
                disabled={inactive}
              />
              {/* 
                COMMENTED FOLLOWING INSTRUCTION ON TICKET EM-75
                <Button
                icon="pi pi-trash"
                aria-label="Delete"
                size="small"
                className={styles.rounded_btn}
                style={{ marginLeft: "10px" }}
                severity="danger"
                onClick={() => {
                  setDeleteReferralConfirmationVisible(true)
                }}
              /> */}
            </span>
          )}
        </div>
        {renderDeleteReferralConfirmation()}
        <div className={styles.table_group}>
          {Object.values(basicInfo).map((group, groupIndex) => {
            return (
              <table
                className={styles.table}
                key={`active_referral_table_${groupIndex}`}
              >
                <thead>
                  <tr>
                    {group.map((item, index) => (
                      <th key={index}>{item.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr key={`active_referral${groupIndex}`}>
                    {group.map((item, index) =>
                      renderTableDataCell(item, index),
                    )}
                  </tr>
                </tbody>
              </table>
            )
          })}
        </div>
        <div className={styles.table_group}>
          <h3
            className={styles.view_subtitle}
            style={{
              color: "#383838",
              fontWeight: "600",
              marginBottom: "10px",
            }}
          >
            Additional information
          </h3>
          {activeReferralDic
            .filter((r) => r.group === "additional_info")
            .map((item, index) => {
              return (
                <table
                  className={styles.table}
                  key={`active_referral_additional_${index}`}
                >
                  <thead>
                    <tr>
                      <th>{item.label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={`active_referral_additional_${index}`}>
                      {renderTableDataCell(item)}
                    </tr>
                  </tbody>
                </table>
              )
            })}
        </div>
      </div>
    )
  }
  const renderCompletedReferrals = () => {
    return (
      <div className={styles.rounded_wrapper} style={{ marginTop: "10px" }}>
        <h3 className={styles.view_subtitle} style={{ color: "#383838" }}>
          Completed employment referrals
        </h3>
        {renderReopenConfirmation()}
        <table id="active_referral_completed" className={styles.table}>
          <thead>
            <tr>
              <th>Referral date</th>
              <th>Completed</th>
              <th>Are you currently employed?</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {completedReferrals && completedReferrals.length > 0 ? (
              completedReferrals.map((item, index) => {
                return (
                  <tr key={index}>
                    {renderTableDataCell({
                      label: "engagement_date",
                      value: formatToLocalDateTime(
                        item.engagementDate,
                        DateFormat,
                      ),
                    })}
                    {renderTableDataCell({
                      label: "completed_at",
                      value: formatToLocalDateTime(
                        item.completedAt,
                        DateFormat,
                      ),
                    })}
                    <td>
                      {" "}
                      {renderTableDataCell({
                        label: "currenty_employed",
                        value:
                          item.transitionPlan.currentyEmployed !== null
                            ? YesNo[item.transitionPlan.currentyEmployed]
                            : "",
                      })}
                    </td>
                    <td>
                      {renderReferalActions(
                        item,
                        index === completedReferrals.length - 1,
                      )}
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="4" style={{ paddingTop: "10px" }}>
                  No employment referrals have been completed
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }
  const checkTransitionPlanAvailability = () => {
    if (
      selectedReferral &&
      !transitionPlan &&
      selectedReferral.transitionPlan
    ) {
      return true
    }
    if (viewMode && !transitionPlan) {
      return true
    }
    return false
  }
  const referralCloseCallback = (data) => {
    const updatedRecords = referrals.map((r) => {
      if (r.id === data.id) {
        return data
      }
      return r
    })
    setReferrals(updatedRecords)
    updateIsActiveReferral(false)
  }
  return (
    <>
      <Toast ref={toast} position="top-right" />
      {loading && (
        <div>
          <LoaderMedium />
        </div>
      )}
      {!loading && !showTransitionPlan && (
        <div className={styles.flex_card}>
          {(activeReferral != null ||
            (referralFormAction === Actions.View && viewMode)) &&
            renderActiveReferral()}
          {completedReferrals?.length > 0 &&
            !viewMode &&
            renderCompletedReferrals()}
        </div>
      )}
      {!loading && showReferralForm && !viewMode && (
        <EmploymentReferral
          action={referralFormAction}
          closeForm={() => {
            setShowReferralForm(false)
            setViewMode(null)
          }}
          referral={selectedReferral}
          updateData={(data) => {
            setViewMode(null)
            updateData(data)
          }}
          viewMode={viewMode}
        />
      )}
      {!loading && showTransitionPlan && (
        <TransitionPlan
          closeForm={() => {
            setShowTransitionPlan(false)
            setTransitionPlan(null)
          }}
          referral={
            viewMode ? selectedReferral : (activeReferral ?? selectedReferral)
          }
          updateData={(data) => {
            referralCloseCallback(data)
          }}
          action={Actions.Add}
          viewMode={viewMode}
        />
      )}
    </>
  )
}
